import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TournamentCard } from "../Tournament/TournamentCard";
import { NewsClient, NewsModel, TournamentNewsItem } from '../../utils/apiClient';
import axios from 'axios';
import ApiUrl from '../../utils/config';
import AppContext from '../../AppContext';
import { handleError } from '../../utils';

// Декоративная турнирная сетка для хиро-блока — просто иллюстрация формата "на выбывание",
// без привязки к реальным данным.
const BracketGraphic = () => (
  <svg width="100%" height="260" viewBox="0 0 380 260" fill="none" aria-hidden="true">
    <line x1="0" y1="16" x2="80" y2="16" stroke="rgba(201,111,60,0.65)" strokeWidth={2} />
    <line x1="0" y1="48" x2="80" y2="48" stroke="rgba(201,111,60,0.65)" strokeWidth={2} />
    <line x1="80" y1="16" x2="80" y2="48" stroke="rgba(201,111,60,0.65)" strokeWidth={2} />
    <line x1="80" y1="32" x2="126" y2="32" stroke="rgba(201,111,60,0.65)" strokeWidth={2} />

    <line x1="0" y1="84" x2="80" y2="84" stroke="rgba(201,111,60,0.65)" strokeWidth={2} />
    <line x1="0" y1="116" x2="80" y2="116" stroke="rgba(201,111,60,0.65)" strokeWidth={2} />
    <line x1="80" y1="84" x2="80" y2="116" stroke="rgba(201,111,60,0.65)" strokeWidth={2} />
    <line x1="80" y1="100" x2="126" y2="100" stroke="rgba(201,111,60,0.65)" strokeWidth={2} />

    <line x1="0" y1="152" x2="80" y2="152" stroke="rgba(255,255,255,0.22)" strokeWidth={2} />
    <line x1="0" y1="184" x2="80" y2="184" stroke="rgba(255,255,255,0.22)" strokeWidth={2} />
    <line x1="80" y1="152" x2="80" y2="184" stroke="rgba(255,255,255,0.22)" strokeWidth={2} />
    <line x1="80" y1="168" x2="126" y2="168" stroke="rgba(255,255,255,0.22)" strokeWidth={2} />

    <line x1="0" y1="220" x2="80" y2="220" stroke="rgba(255,255,255,0.22)" strokeWidth={2} />
    <line x1="0" y1="252" x2="80" y2="252" stroke="rgba(255,255,255,0.22)" strokeWidth={2} />
    <line x1="80" y1="220" x2="80" y2="252" stroke="rgba(255,255,255,0.22)" strokeWidth={2} />
    <line x1="80" y1="236" x2="126" y2="236" stroke="rgba(255,255,255,0.22)" strokeWidth={2} />

    <line x1="126" y1="32" x2="126" y2="100" stroke="rgba(201,111,60,0.65)" strokeWidth={2} />
    <line x1="126" y1="66" x2="196" y2="66" stroke="rgba(201,111,60,0.65)" strokeWidth={2} />

    <line x1="126" y1="168" x2="126" y2="236" stroke="rgba(255,255,255,0.22)" strokeWidth={2} />
    <line x1="126" y1="202" x2="196" y2="202" stroke="rgba(255,255,255,0.22)" strokeWidth={2} />

    <line x1="196" y1="66" x2="196" y2="202" stroke="rgba(250,247,242,0.35)" strokeWidth={2} />
    <line x1="196" y1="134" x2="270" y2="134" stroke="#c96f3c" strokeWidth={2.5} />
    <circle cx="270" cy="134" r="4" fill="#c96f3c" />
    <text x="200" y="124" fontFamily="Oswald, sans-serif" fontWeight={600} fontSize="11" letterSpacing="0.08em" fill="#e0a074">ФИНАЛ</text>
  </svg>
);

export const HomePage = () => {
  const appContext = React.useContext(AppContext);
  const [news, setNews] = useState<NewsModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const newsClient = new NewsClient(ApiUrl, axios);

  const getNews = async () => {
    try {
      setLoading(true);
      const response = await newsClient.getNews();
      setNews(response);
    } catch (ex) {
      handleError(appContext!.setNotificationData, ex);
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    getNews();
  }, []);

  const active = news?.activeTournaments ?? [];
  const upcoming = news?.upcomingTournaments ?? [];
  const past = news?.pastTournaments ?? [];

  const regionsCount = new Set(
    [...active, ...upcoming, ...past]
      .map((t: TournamentNewsItem) => t.location)
      .filter((location): location is string => Boolean(location))
  ).size;

  const stats = [
    { value: String(active.length), label: 'турниров активно' },
    { value: String(upcoming.length), label: 'скоро стартуют' },
    { value: String(regionsCount), label: 'городов на карте' },
  ];

  return (
    <div className="home-page">
      <section className="home-page__hero">
        <div className="home-page__hero-content">
          {news && (
            <div className="home-page__stats">
              {stats.map((stat) => (
                <div className="home-page__stat" key={stat.label}>
                  <span className="home-page__stat-value">{stat.value}</span>
                  <span className="home-page__stat-label">{stat.label}</span>
                </div>
              ))}
            </div>
          )}
          <h1 className="home-page__title">Турниры по дзюдо.<br />Организовано как надо.</h1>
          <p className="home-page__subtitle">Регистрация участников, турнирные сетки, live-судейство и протоколы — всё в одном месте, без бумаги и путаницы.</p>
          <div className="home-page__cta">
            <Link to="/tournaments" className="home-page__cta-primary">Смотреть турниры</Link>
            <a href="#how-it-works" className="home-page__cta-secondary">Как это работает</a>
          </div>
        </div>
        <div className="home-page__hero-graphic">
          <BracketGraphic />
        </div>
      </section>

      {loading && <p className="home-page__status">Загружаем турниры...</p>}
      {!loading && !news && <p className="home-page__status">Не удалось загрузить турниры. Попробуйте обновить страницу.</p>}

      {news && (
        <section className="home-page__tournaments">
          <div className="home-page__section-header">
            <h2 className="home-page__section-title">Турниры</h2>
            <Link to="/tournaments" className="home-page__section-link">Все турниры →</Link>
          </div>

          <div className="home-page__tournament-group">
            <h3 className="home-page__group-title">Активные</h3>
            {active.length > 0 ? (
              <div className="home-page__tournament-list">
                {active.map((tournament) => (
                  <TournamentCard key={tournament.id} tournament={tournament} status="active" />
                ))}
              </div>
            ) : (
              <p className="home-page__empty">Сейчас нет турниров с открытой регистрацией</p>
            )}
          </div>

          <div className="home-page__tournament-group">
            <h3 className="home-page__group-title">Предстоящие</h3>
            {upcoming.length > 0 ? (
              <div className="home-page__tournament-list">
                {upcoming.map((tournament) => (
                  <TournamentCard key={tournament.id} tournament={tournament} status="upcoming" />
                ))}
              </div>
            ) : (
              <p className="home-page__empty">Пока новых турниров не запланировано</p>
            )}
          </div>

          <div className="home-page__tournament-group">
            <h3 className="home-page__group-title">Прошедшие</h3>
            {past.length > 0 ? (
              <div className="home-page__tournament-list">
                {past.map((tournament) => (
                  <TournamentCard key={tournament.id} tournament={tournament} status="past" />
                ))}
              </div>
            ) : (
              <p className="home-page__empty">Турнирная история пока пуста</p>
            )}
          </div>
        </section>
      )}

      <section id="how-it-works" className="home-page__how">
        <h2 className="home-page__section-title home-page__section-title--center">Как это работает</h2>
        <div className="home-page__steps">
          <div className="home-page__step">
            <span className="home-page__step-number">1</span>
            <h3>Регистрация</h3>
            <p>Найдите турнир и подайте заявку в пару кликов.</p>
          </div>
          <div className="home-page__step">
            <span className="home-page__step-number">2</span>
            <h3>Участие</h3>
            <p>Сетка формируется автоматически, судьи ведут live-протокол прямо с татами.</p>
          </div>
          <div className="home-page__step">
            <span className="home-page__step-number">3</span>
            <h3>Результаты</h3>
            <p>Протоколы и итоговые места — сразу после финальной схватки.</p>
          </div>
        </div>
      </section>
    </div>
  );
};
